<template>
  <v-card class="pyg-table-card elevation-12 rounded-top">
    <v-card-title class="pb-2">
      <span>Detalle de Partidas</span>
      <v-spacer></v-spacer>
      <v-checkbox
        v-model="showEmptyRows"
        label="Mostrar conceptos sin importe"
        hide-details
        dense
        class="mt-0 mr-4 shrink"
      ></v-checkbox>
      <v-btn
        small
        color="primary"
        outlined
        @click="exportTable"
        :disabled="!tableData || tableData.length === 0"
      >
        <v-icon left small>mdi-download</v-icon>
        Exportar CSV
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <v-data-table
        :headers="headers"
        :items="filteredTableData"
        :items-per-page="-1"
        :sort-by="['clave_num']"
        :sort-desc="[false]"
        hide-default-footer
        class="pyg-data-table elevation-0"
        :item-class="getRowClass"
      >
        <!-- HEADER TOOLTIPS -->
        <!-- Single-year mode: Percentage column tooltip -->
        <template v-if="!isMultiYear" v-slot:header.percentage="{ header }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" style="cursor: help;">
                {{ header.text }}
                <v-icon small color="grey">mdi-information-outline</v-icon>
              </span>
            </template>
            <span>Porcentaje que representa cada concepto sobre el Importe neto de ventas (base 100%)</span>
          </v-tooltip>
        </template>

        <!-- Multi-year mode: Percentage columns tooltip (dynamic) -->
        <template v-if="isMultiYear" v-for="year in selectedEjercicios" v-slot:[`header.percentage_${year}`]="{ header }">
          <v-tooltip :key="`tooltip_percentage_${year}`" bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" style="cursor: help;">
                {{ header.text }}
                <v-icon small color="grey">mdi-information-outline</v-icon>
              </span>
            </template>
            <span>Porcentaje que representa cada concepto sobre el Importe neto de ventas de {{ year }} (base 100%)</span>
          </v-tooltip>
        </template>

        <!-- Multi-year mode: Variation column tooltip -->
        <template v-if="isMultiYear && selectedEjercicios.length === 2" v-slot:header.variation="{ header }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" style="cursor: help;">
                {{ header.text }}
                <v-icon small color="grey">mdi-information-outline</v-icon>
              </span>
            </template>
            <span>Variación porcentual entre {{ selectedEjercicios[0] }} y {{ selectedEjercicios[1] }}. Fórmula: ((Año2 - Año1) / |Año1|) × 100</span>
          </v-tooltip>
        </template>

        <!-- Código column -->
        <template v-slot:item.clave_num="{ item }">
          <span
            v-if="item.isResult"
            class="result-label"
          >
            RESULTADO
          </span>
          <span
            v-else
            class="text--secondary monospace-font"
          >
            {{ item.clave_num }}
          </span>
        </template>

        <!-- Concept column -->
        <template v-slot:item.concept="{ item }">
          <span
            v-if="!item.isResult"
            :class="{ 'text--disabled': getIsEmpty(item) }"
          >
            {{ item.concept }}
          </span>
        </template>

        <!-- SINGLE-YEAR MODE: Value and Percentage columns -->
        <template v-if="!isMultiYear" v-slot:item.value="{ item }">
          <span
            v-if="!item.isEmpty"
            :class="{
              'font-weight-bold': item.isResult,
              'negative-value': item.value < 0 && !item.isResult,
              'positive-value': item.value >= 0 || item.isResult
            }"
          >
            {{ formatValue(item.value) }}
          </span>
          <span v-else class="text--disabled">-</span>
        </template>

        <template v-if="!isMultiYear" v-slot:item.percentage="{ item }">
          <span
            v-if="!item.isEmpty"
            :class="{ 'font-weight-bold': item.isResult }"
          >
            {{ formatPercentage(item.percentage) }}
          </span>
          <span v-else class="text--disabled">-</span>
        </template>

        <!-- MULTI-YEAR MODE: Dynamic columns per year -->
        <template v-if="isMultiYear" v-for="year in selectedEjercicios" v-slot:[`item.importe_${year}`]="{ item }">
          <span
            :key="`importe_${year}`"
            v-if="item.years && item.years[year] && !item.years[year].isEmpty"
            :class="{
              'font-weight-bold': item.isResult,
              'negative-value': item.years[year].value < 0 && !item.isResult,
              'positive-value': item.years[year].value >= 0 || item.isResult
            }"
          >
            {{ formatValue(item.years[year].value) }}
          </span>
          <span :key="`importe_${year}_empty`" v-else class="text--disabled">-</span>
        </template>

        <template v-if="isMultiYear" v-for="year in selectedEjercicios" v-slot:[`item.percentage_${year}`]="{ item }">
          <span
            :key="`percentage_${year}`"
            v-if="item.years && item.years[year] && !item.years[year].isEmpty"
            :class="{ 'font-weight-bold': item.isResult }"
          >
            {{ formatPercentage(item.years[year].percentage) }}
          </span>
          <span :key="`percentage_${year}_empty`" v-else class="text--disabled">-</span>
        </template>

        <!-- Variation column (only for 2 years) -->
        <template v-if="isMultiYear && selectedEjercicios.length === 2" v-slot:item.variation="{ item }">
          <span
            v-if="item.variation"
            :class="{
              'font-weight-bold': item.isResult,
              'positive-variation': item.variation.percentage >= 0,
              'negative-variation': item.variation.percentage < 0
            }"
          >
            {{ formatVariation(item.variation.percentage) }}
          </span>
          <span v-else class="text--disabled">-</span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { formatNegative, formatPercentage, exportToCSV } from '@/utils/pygFormatters';

export default {
  name: 'PygTable',

  props: {
    tableData: {
      type: Array,
      required: true,
      default: () => []
    },
    selectedEjercicio: {
      type: Number,
      required: false,
      default: null
    },
    selectedEjercicios: {
      type: Array,
      default: () => []
    },
    isMultiYear: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showEmptyRows: false
    };
  },

  computed: {
    /**
     * Filtered table data based on showEmptyRows checkbox
     */
    filteredTableData() {
      if (this.showEmptyRows) {
        return this.tableData;
      }
      return this.tableData.filter(item => !this.getIsEmpty(item));
    },

    headers() {
      if (this.isMultiYear && this.selectedEjercicios && this.selectedEjercicios.length > 1) {
        // MULTI-YEAR MODE: Generate dynamic columns
        const baseHeaders = [
          {
            text: 'Código',
            value: 'clave_num',
            align: 'center',
            sortable: true,
            class: 'header-column',
            width: '10%'
          },
          {
            text: 'Concepto',
            value: 'concept',
            align: 'left',
            sortable: true,
            class: 'header-column',
            width: '25%'
          }
        ];

        // Add columns for each exercise (Importe + %)
        this.selectedEjercicios.forEach(year => {
          baseHeaders.push(
            {
              text: `Importe (${year})`,
              value: `importe_${year}`,
              align: 'right',
              sortable: true,
              class: 'header-column',
              width: `${30 / this.selectedEjercicios.length}%`
            },
            {
              text: `%`,
              value: `percentage_${year}`,
              align: 'right',
              sortable: true,
              class: 'header-column',
              width: `${15 / this.selectedEjercicios.length}%`
            }
          );
        });

        // Add variation column if exactly 2 years
        if (this.selectedEjercicios.length === 2) {
          baseHeaders.push({
            text: 'Variación',
            value: 'variation',
            align: 'right',
            sortable: true,
            class: 'header-column',
            width: '10%'
          });
        }

        return baseHeaders;
      } else {
        // SINGLE-YEAR MODE: Current behavior
        return [
          {
            text: 'Código',
            value: 'clave_num',
            align: 'center',
            sortable: true,
            class: 'header-column',
            width: '10%'
          },
          {
            text: 'Concepto',
            value: 'concept',
            align: 'left',
            sortable: true,
            class: 'header-column',
            width: '40%'
          },
          {
            text: `Importe (${this.selectedEjercicio || ''})`,
            value: 'value',
            align: 'right',
            sortable: true,
            class: 'header-column',
            width: '25%'
          },
          {
            text: '%',
            value: 'percentage',
            align: 'right',
            sortable: true,
            class: 'header-column',
            width: '25%'
          }
        ];
      }
    }
  },

  methods: {
    /**
     * Format value with parentheses for negatives
     */
    formatValue(value) {
      return formatNegative(value, false);
    },

    /**
     * Format percentage with 1 decimal
     */
    formatPercentage(value) {
      return formatPercentage(value);
    },

    /**
     * Format variation percentage with + or - sign
     */
    formatVariation(percentage) {
      const formatted = percentage.toFixed(1);
      return percentage >= 0 ? `+${formatted}%` : `${formatted}%`;
    },

    /**
     * Check if item is empty (handles both single and multi-year)
     */
    getIsEmpty(item) {
      if (this.isMultiYear) {
        // Check if all years are empty
        if (!item.years) return true;
        return Object.values(item.years).every(yearData => yearData.isEmpty);
      }
      return item.isEmpty;
    },

    /**
     * Get CSS class for table row
     */
    getRowClass(item) {
      if (item.isResult) {
        return 'result-row';
      }
      if (this.getIsEmpty(item)) {
        return 'empty-row';
      }
      return '';
    },

    /**
     * Export table data to CSV (adapts to single-year or multi-year mode)
     */
    exportTable() {
      if (!this.tableData || this.tableData.length === 0) {
        return;
      }

      let exportData;
      let filename;

      if (this.isMultiYear && this.selectedEjercicios && this.selectedEjercicios.length > 1) {
        // MULTI-YEAR MODE: Export columns for each year
        const sortedYears = [...this.selectedEjercicios].sort((a, b) => a - b);

        exportData = this.tableData.map(row => {
          const rowData = {
            'Concepto': row.concept
          };

          // Add Importe and % columns for each year
          sortedYears.forEach(year => {
            const yearData = row.years && row.years[year];
            const isEmpty = !yearData || yearData.isEmpty;

            rowData[`Importe (${year})`] = isEmpty ? '-' : this.formatValue(yearData.value);
            rowData[`% (${year})`] = isEmpty ? '-' : this.formatPercentage(yearData.percentage);
          });

          // Add Variation column if exactly 2 years
          if (sortedYears.length === 2 && row.variation) {
            rowData['Variación'] = this.formatVariation(row.variation.percentage);
          }

          return rowData;
        });

        filename = `cuenta_resultados_${sortedYears.join('_')}.csv`;
      } else {
        // SINGLE-YEAR MODE: Export standard 3 columns
        exportData = this.tableData.map(row => ({
          'Concepto': row.concept,
          'Importe': row.isEmpty ? '-' : this.formatValue(row.value),
          '%': row.isEmpty ? '-' : this.formatPercentage(row.percentage)
        }));

        filename = `cuenta_resultados_${this.selectedEjercicio}.csv`;
      }

      exportToCSV(exportData, filename);
    }
  }
}
</script>

<style scoped>
.pyg-table-card {
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
}

.pyg-table-card .v-card__title {
  color: var(--v-primary-base);
  font-size: 18px;
  font-weight: 500;
  border-bottom: 1px solid #e6e6e6;
  padding: 16px 20px;
}

.pyg-data-table {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

/* Header styling */
.pyg-data-table >>> .v-data-table-header th {
  background-color: #f5f5f5 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  color: #333 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  padding: 12px 16px !important;
}

/* Row styling */
.pyg-data-table >>> tbody tr {
  transition: background-color 0.2s;
}

.pyg-data-table >>> tbody tr:hover {
  background-color: var(--v-neutral-base) !important;
}

.pyg-data-table >>> tbody td {
  padding: 10px 16px !important;
  font-size: 14px !important;
  border-bottom: 1px solid #e6e6e6 !important;
}

/* Result row styling */
.pyg-data-table >>> tbody tr.result-row {
  background-color: #f0f0f0 !important;
  border-top: 2px solid var(--v-primary-base) !important;
}

.pyg-data-table >>> tbody tr.result-row td {
  font-weight: 700 !important;
  font-size: 15px !important;
  color: var(--v-primary-base) !important;
  padding: 14px 16px !important;
}

/* Empty row styling */
.pyg-data-table >>> tbody tr.empty-row {
  opacity: 0.5;
}

/* Value styling */
.negative-value {
  color: #333;
}

.positive-value {
  color: #333;
}

/* Monospace font for codes */
.monospace-font {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  letter-spacing: 0.5px;
}

/* Result label styling */
.result-label {
  font-weight: 700;
  font-size: 15px;
  color: var(--v-primary-base);
  text-align: center;
  display: block;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .pyg-data-table >>> tbody td,
  .pyg-data-table >>> .v-data-table-header th {
    font-size: 12px !important;
    padding: 8px 12px !important;
  }

  .pyg-table-card .v-card__title {
    font-size: 16px;
    padding: 12px 16px;
  }
}

@media (max-width: 600px) {
  .pyg-data-table {
    font-size: 11px;
  }

  .pyg-data-table >>> tbody td,
  .pyg-data-table >>> .v-data-table-header th {
    padding: 6px 8px !important;
  }
}
</style>
